import { useEffect, useRef, useState } from 'react'
import FinviseDateRangeDialog from '../models/FinviseDateRangeDialog'
import { dayEnd, dayStart, fmtShort } from './finviseMetrics'

export const PRESETS = [
	{
		id: 'MTD',
		name: 'Month to date',
		hint: 'MTD',
		calc: now => [new Date(now.getFullYear(), now.getMonth(), 1), now],
	},
	{
		id: 'QTD',
		name: 'Quarter to date',
		hint: 'QTD',
		calc: now => [
			new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1),
			now,
		],
	},
	{
		id: 'YTD',
		name: 'Year to date',
		hint: 'YTD',
		calc: now => [new Date(now.getFullYear(), 0, 1), now],
	},
	{
		id: 'L30',
		name: 'Last 30 days',
		hint: '',
		calc: now => [new Date(+now - 29 * 864e5), now],
	},
	{
		id: 'L90',
		name: 'Last 90 days',
		hint: '',
		calc: now => [new Date(+now - 89 * 864e5), now],
	},
	{
		id: 'L12M',
		name: 'Last 12 months',
		hint: '',
		calc: now => [
			new Date(now.getFullYear() - 1, now.getMonth(), now.getDate() + 1),
			now,
		],
	},
	{
		id: 'ALL',
		name: 'All time',
		hint: '',
		calc: (now, minDate) => [minDate || now, now],
	},
]

const FinviseRangePicker = props => {
	const now = props.now || new Date()
	const minDate =
		props.minDate ||
		new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
	const onChange = props.onChange

	const [open, setOpen] = useState(false)
	const wrapRef = useRef(null)

	useEffect(() => {
		const onDoc = e => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
		}
		const onEsc = e => {
			if (e.key === 'Escape') setOpen(false)
		}
		document.addEventListener('click', onDoc)
		document.addEventListener('keydown', onEsc)
		return () => {
			document.removeEventListener('click', onDoc)
			document.removeEventListener('keydown', onEsc)
		}
	}, [])

	const selectPreset = p => {
		const [f, t] = p.calc(now, minDate)
		onChange({ from: dayStart(f), to: dayEnd(t), label: p.name })
		setOpen(false)
	}

	const handleCustomClose = (start, end) => {
		let from = dayStart(new Date(start))
		let to = dayEnd(new Date(end))
		if (+from > +to) {
			from = dayStart(new Date(end))
			to = dayEnd(new Date(start))
		}
		onChange({ from, to, label: 'Custom range' })
		setOpen(false)
	}

	const label = (props.value && props.value.label) || 'Select range'
	const dates =
		props.value && props.value.from && props.value.to
			? `${fmtShort(props.value.from)} \u2013 ${fmtShort(props.value.to)}`
			: ''

	return (
		<div className='range-wrap' ref={wrapRef}>
			<button
				className='range-btn'
				aria-haspopup='true'
				aria-expanded={open}
				onClick={e => {
					e.stopPropagation()
					setOpen(!open)
				}}
			>
				<svg
					className='cal-ico'
					width='15'
					height='15'
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth='1.9'
					strokeLinecap='round'
					strokeLinejoin='round'
				>
					<rect x='3' y='4' width='18' height='18' rx='2' />
					<path d='M16 2v4M8 2v4M3 10h18' />
				</svg>
				<span className='preset-name'>{label}</span>
				<span className='dates'>{dates}</span>
			</button>

			{open ? (
				<div className='range-pop open' role='menu'>
					{PRESETS.map(p => {
						const selected = props.value && props.value.label === p.name
						return (
							<button
								key={p.id}
								className={'row' + (selected ? ' selected' : '')}
								role='menuitem'
								onClick={() => selectPreset(p)}
							>
								<span>
									{p.name}
									{p.hint ? <span className='hint'>{p.hint}</span> : null}
								</span>
								<span className='check'>{'\u2713'}</span>
							</button>
						)
					})}

					<FinviseDateRangeDialog
						initialFrom={props.value ? props.value.from : now}
						initialTo={props.value ? props.value.to : now}
						handleClose={handleCustomClose}
					/>
				</div>
			) : null}
		</div>
	)
}

export default FinviseRangePicker
